/**
 * NotificationToast - Toast notifications for real-time alerts
 *
 * Displays toast notifications at the bottom-right of the screen
 * for important events like batch job completions.
 */

import { Alert,AlertTitle,Box,Snackbar,Typography } from '@mui/material'
import React,{ useEffect,useState } from 'react'
import { AppNotification,useNotifications } from '../../contexts/NotificationContext'

interface ToastItem {
  notification: AppNotification
  open: boolean
}

export const NotificationToast: React.FC = () => {
  const { notifications } = useNotifications()
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [lastNotificationId, setLastNotificationId] = useState<string | null>(null)

  // Watch for new notifications and show toast
  useEffect(() => {
    if (notifications.length === 0) return

    const latestNotification = notifications[0]

    // Only show toast for new notifications
    if (latestNotification.id !== lastNotificationId && !latestNotification.read) {
      setLastNotificationId(latestNotification.id)

      // Add to toasts
      setToasts(prev => [
        ...prev,
        { notification: latestNotification, open: true }
      ])

      // Auto-dismiss after 6 seconds
      setTimeout(() => {
        setToasts(prev =>
          prev.map(t =>
            t.notification.id === latestNotification.id
              ? { ...t, open: false }
              : t
          )
        )
      }, 6000)

      // Remove from list after animation
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.notification.id !== latestNotification.id))
      }, 6500)
    }
  }, [notifications, lastNotificationId])

  const handleClose = (notificationId: string) => {
    setToasts(prev =>
      prev.map(t =>
        t.notification.id === notificationId
          ? { ...t, open: false }
          : t
      )
    )
  }

  const getSeverity = (type: AppNotification['type']) => {
    switch (type) {
      case 'success': return 'success'
      case 'error': return 'error'
      case 'warning': return 'warning'
      default: return 'info'
    }
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1,
        maxWidth: 400,
      }}
    >
      {toasts.map((toast, _index) => (
        <Snackbar
          key={toast.notification.id}
          open={toast.open}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            position: 'relative',
            transform: 'none !important',
            bottom: 'auto !important',
            right: 'auto !important',
          }}
        >
          <Alert
            severity={getSeverity(toast.notification.type)}
            variant="filled"
            onClose={() => handleClose(toast.notification.id)}
            sx={{
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>
              {toast.notification.title}
            </AlertTitle>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {toast.notification.message}
            </Typography>
          </Alert>
        </Snackbar>
      ))}
    </Box>
  )
}

export default NotificationToast

